import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, TrendingDown, BarChart3, Shield, 
  Check, Rocket, Star, Quote, ChevronDown
} from 'lucide-react';
import { CodeText } from '../components/TextAnimations';
import { useTranslation } from 'react-i18next';

// Key Benefits avec animations code
export const CallUpKeyBenefits = () => {
  const { t } = useTranslation();
  const benefits = [
    { icon: Clock, titleKey: 'benefits.time', descKey: 'benefits.time_desc', stat: '80%', color: 'from-cyan-500/10 to-sky-600/10' },
    { icon: TrendingDown, titleKey: 'benefits.savings', descKey: 'benefits.savings_desc', stat: '60%', color: 'from-indigo-500/10 to-violet-600/10' },
    { icon: BarChart3, titleKey: 'benefits.insights', descKey: 'benefits.insights_desc', stat: '100%', color: 'from-violet-500/10 to-purple-600/10' },
    { icon: Shield, titleKey: 'benefits.security', descKey: 'benefits.security_desc', stat: 'RGPD', color: 'from-purple-600/10 to-fuchsia-600/10' }
  ];

  return (
    <section id="benefits" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-black mb-3 tracking-tight">
            <CodeText text={t('benefits.title')} className="gradient-callup" delay={0} />
          </h2>
          <p className="text-gray-400 text-lg">{t('benefits.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-2xl p-6 rounded-2xl transition-colors duration-300 hover:bg-white/10"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.color} backdrop-blur-md flex items-center justify-center mb-4 border border-white/20`}>
                <benefit.icon className="w-6 h-6 text-white drop-shadow-lg" />
              </div>
              <div className="text-4xl font-black gradient-callup mb-2" style={{ letterSpacing: '0.03em' }}>
                {benefit.stat}
              </div>
              <h3 className="text-lg font-bold text-white mb-2 tracking-wide">{t(benefit.titleKey)}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{t(benefit.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing avec animation code et traduit en français
// Pricing MINIMALISTE
export const CallUpPricing = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (planName, price) => {
    if (price === 'Sur devis') {
      window.location.href = '#calendly';
      return;
    }

    try {
      setLoading(planName);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: `${planName.toLowerCase()}_plan`,
          success_url: `${window.location.origin}/dashboard?subscription=success`,
          cancel_url: `${window.location.origin}/?subscription=cancelled`,
          mode: 'subscription',
          line_items: [{
            price_data: {
              currency: 'eur',
              product_data: {
                name: `Plan ${planName} Call Up`,
                description: `Abonnement mensuel ${planName}`
              },
              unit_amount: price * 100,
              recurring: { interval: 'month' }
            },
            quantity: 1
          }]
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erreur checkout:', error);
      alert(t('pricing.paymentError'));
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: 79,
      agents: '1 Agent IA',
      minutes: '120 min/mois',
      features: ['Réponse automatique 24/7', 'Prise de RDV intelligente', 'Confirmation SMS', 'Dashboard Analytics', 'Support Email']
    },
    {
      name: 'Premium',
      price: 139,
      agents: '2 Agents IA',
      minutes: '400 min/mois',
      features: ['Tout Starter +', 'Multi-agents', 'Intégrations CRM', 'Rapports avancés', 'Support Prioritaire', 'Personnalisation voix'],
      popular: true
    },
    {
      name: 'Business',
      price: 'Sur devis',
      agents: 'Illimité',
      minutes: 'À définir',
      features: ['Tout Premium +', 'API complète', 'Déploiement on-premise', 'SLA garanti', 'Account Manager dédié', 'Formation équipe']
    }
  ];

  return (
    <section id="pricing" className="py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-black mb-4"><CodeText text={t('header.pricing')} className="gradient-callup" delay={0} /></h2>
          <p className="text-gray-400 text-lg">{t('pricing.chooseYourPlan')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, i) => (
            <div key={i} className={`relative bg-white/[0.02] backdrop-blur-sm border rounded-2xl p-8 transition-colors duration-300 hover:bg-white/[0.04] ${plan.popular ? 'border-cyan-500/50' : 'border-white/10 hover:border-white/20'}`}>
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"><span className="text-white text-xs font-bold tracking-wide">{t('pricing.mostPopular')}</span></div>}
              <div className="mb-8">
                <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">{typeof plan.price === 'number' ? <><span className="text-5xl font-black text-white">{plan.price}€</span><span className="text-gray-500">{t('pricing.perMonth')}</span></> : <span className="text-3xl font-black text-white">{plan.price}</span>}</div>
                <div className="space-y-1 text-sm"><p className="text-cyan-400 font-semibold">{plan.agents}</p><p className="text-gray-400">{plan.minutes}</p></div>
              </div>
              <div className="h-px bg-white/10 mb-8" />
              <ul className="space-y-3 mb-8">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-3 text-sm"><div className="w-5 h-5 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-cyan-400" strokeWidth={3} /></div><span className="text-gray-300">{f}</span></li>)}</ul>
              <button 
                onClick={() => handleCheckout(plan.name, plan.price)}
                disabled={loading === plan.name}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${plan.popular ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20'}`}
              >
                {loading === plan.name ? t('common.loading') : t('pricing.selectPlan')}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-gray-500 text-sm mt-12">*Tous les prix sont HT</p>
      </div>
    </section>
  );
};

// Options, Packs minutes & Services
export const CallUpAddons = () => {
  const addons = [
    {
      name: 'Analytics avancés',
      price: '+29 €/mois',
      items: ['Nombre d\'appels', 'Heures de pointe', 'Taux de conversion', 'Historique détaillé']
    },
    {
      name: 'Transcripts complets',
      price: '+19 €/mois',
      items: ['Historique complet', 'Recherche par mot-clé', 'Export']
    },
    {
      name: 'Numéro supplémentaire',
      price: '+19 €/mois',
      items: []
    },
    {
      name: 'Gel du compte (Freeze)',
      price: '+9 €/mois',
      items: ['Conservation du numéro', 'Conservation des scénarios', 'Suspension temporaire']
    }
  ];

  const minutePacks = [
    { name: 'Pack 100 min', price: '49 €' },
    { name: 'Pack 250 min', price: '99 €' },
    { name: 'Pack 500 min', price: '179 €' },
    { name: 'Hors forfait', price: '0,50 €/min' }
  ];

  const services = [
    {
      name: 'Installation standard',
      price: '199 €',
      items: ['Paramétrage IA', 'Création du scénario', 'Tests', 'Mise en service']
    },
    {
      name: 'Formation visio',
      price: '149 €',
      items: ['1 heure', 'Prise en main', 'Optimisation']
    },
    {
      name: 'Formation premium visio',
      price: '299 €',
      items: ['2 heures', 'Optimisation avancée', 'Stratégie téléphonique']
    },
    {
      name: 'Formation sur site',
      price: '499 €',
      items: ['Demi-journée', 'Équipe complète']
    },
    {
      name: 'Audit téléphonique',
      price: '99 €',
      items: ['Analyse des appels', 'Recommandations', 'Rapport']
    }
  ];

  const Card = ({ name, price, items }) => (
    <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-6 transition-colors duration-300 hover:bg-white/[0.04] hover:border-white/20">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h4 className="text-lg font-bold text-white">{name}</h4>
        <span className="text-cyan-400 font-black whitespace-nowrap">{price}</span>
      </div>
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it, j) => (
            <li key={j} className="flex items-center gap-2 text-sm text-gray-300">
              <div className="w-4 h-4 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-cyan-400" strokeWidth={3} />
              </div>
              {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <section id="options" className="py-32 px-4">
      <div className="max-w-7xl mx-auto space-y-24">
        {/* Add-ons */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <CodeText text="Options" className="gradient-callup" delay={0} />
            </h2>
            <p className="text-gray-400 text-lg">Personnalisez votre forfait</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {addons.map((a, i) => <Card key={i} {...a} />)}
          </div>
        </div>

        {/* Minute packs */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <CodeText text="Packs minutes" className="gradient-callup" delay={0} />
            </h2>
            <p className="text-gray-400 text-lg">Rechargez quand vous voulez</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {minutePacks.map((p, i) => <Card key={i} {...p} />)}
          </div>
          <p className="text-center text-gray-500 text-sm mt-8 italic">
            Les packs minutes sont conçus pour éviter le hors forfait.
          </p>
        </div>

        {/* Services */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <CodeText text="Services" className="gradient-callup" delay={0} />
            </h2>
            <p className="text-gray-400 text-lg">Installation, formation et audit</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => <Card key={i} {...s} />)}
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm">*Tous les prix sont HT</p>
      </div>
    </section>
  );
};

// Testimonials disposition ludique - Grid stagger avec animations
export const CallUpTestimonials = () => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const testimonials = [
    {
      name: 'Marie Dubois',
      role: 'CEO, Restaurant Le Gourmet',
      image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=150&h=150&fit=crop',
      quote: 'Call Up a transformé notre gestion des réservations. Gain de temps énorme !',
      rating: 5,
      metric: '+40%',
      color: 'from-indigo-500 to-violet-600'
    },
    {
      name: 'Thomas Martin',
      role: 'Gérant, Salon Beauté Plus',
      image: 'https://images.unsplash.com/photo-1762522927402-f390672558d8?w=150&h=150&fit=crop',
      quote: 'L\'agent IA gère nos RDV 24/7 et libère du temps pour notre équipe.',
      rating: 5,
      metric: '15h/sem',
      color: 'from-cyan-500 to-sky-600'
    },
    {
      name: 'Sophie Laurent',
      role: 'Directrice, Cabinet Juridique',
      image: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=150&h=150&fit=crop',
      quote: 'Les analytics nous aident à optimiser nos créneaux horaires.',
      rating: 5,
      metric: '89%',
      color: 'from-violet-500 to-purple-600'
    },
    {
      name: 'Pierre Durand',
      role: 'Fondateur, Clinique Dentaire',
      image: 'https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=150&h=150&fit=crop',
      quote: 'Fini les no-shows ! L\'IA confirme automatiquement les RDV.',
      rating: 5,
      metric: '-60%',
      color: 'from-purple-600 to-fuchsia-600'
    }
  ];

  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background decorations */}
      <motion.div
        className="absolute top-20 left-10 w-64 h-64 rounded-full bg-purple-500/5 blur-3xl"
        animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-64 h-64 rounded-full bg-sky-500/5 blur-3xl"
        animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black mb-3 tracking-tight">
            <CodeText text={t('testimonials.title')} className="gradient-callup" delay={0} />
          </h2>
          <p className="text-gray-300 text-xl font-medium tracking-wide">{t('testimonials.subtitle')}</p>
        </motion.div>

        {/* Grid 2x2 avec animations stagger */}
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30, rotateY: -10 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              onHoverStart={() => setHoveredIndex(i)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="relative"
            >
              <motion.div
                className={`card bg-gradient-to-br ${testimonial.color} p-6 relative overflow-hidden cursor-pointer h-full`}
                animate={{
                  scale: hoveredIndex === i ? 1.05 : 1,
                  y: hoveredIndex === i ? -10 : 0
                }}
                whileHover={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                transition={{ duration: 0.3 }}
              >
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 bg-white/10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hoveredIndex === i ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                />

                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <motion.img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full border-2 border-white shadow-lg object-cover object-center"
                        animate={{ scale: hoveredIndex === i ? 1.15 : 1 }}
                        transition={{ duration: 0.3 }}
                      />
                      <div>
                        <h4 className="text-lg font-bold text-white">{testimonial.name}</h4>
                        <p className="text-sm font-medium text-white/90">{testimonial.role}</p>
                      </div>
                    </div>
                    {/* Stars */}
                    <div className="flex gap-0.5">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <motion.div
                          key={j}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.15 + j * 0.1 }}
                        >
                          <Star className="w-4 h-4 text-yellow-300 fill-current" />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Quote icon */}
                  <Quote className="w-8 h-8 text-white/20 mb-3" />
                  
                  {/* Quote text */}
                  <p className="text-white text-base md:text-lg mb-4 leading-relaxed font-medium">
                    "{testimonial.quote}"
                  </p>
                  
                  {/* Metric badge */}
                  <motion.div 
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg"
                    animate={{
                      scale: hoveredIndex === i ? 1.1 : 1
                    }}
                  >
                    <TrendingDown className="w-4 h-4 text-white rotate-180" />
                    <span className="text-sm font-bold text-white" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
                      {testimonial.metric}
                    </span>
                  </motion.div>
                </div>

                {/* Floating particles */}
                {hoveredIndex === i && (
                  <>
                    {[...Array(3)].map((_, j) => (
                      <motion.div
                        key={j}
                        className="absolute w-2 h-2 rounded-full bg-white"
                        initial={{
                          x: (j - 1) * 25,
                          y: 50,
                          opacity: 0
                        }}
                        animate={{ 
                          y: -100,
                          opacity: [0, 1, 0]
                        }}
                        transition={{ 
                          duration: 1.5,
                          delay: j * 0.2,
                          repeat: Infinity
                        }}
                        style={{
                          left: `${30 + j * 30}%`,
                          bottom: '20%'
                        }}
                      />
                    ))}
                  </>
                )}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ avec animation code
export const CallUpFAQ = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  
  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
  ];

  return (
    <section id="faq" className="py-20 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl sm:text-4xl font-black mb-2">
            <CodeText text="FAQ" className="gradient-callup" delay={0} />
          </h2>
          <p className="text-gray-400">{t('faq.subtitle')}</p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="card cursor-pointer"
              onClick={() => setOpen(open === i ? null : i)}
            >
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-white text-sm sm:text-base">{faq.q}</h4>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </div>
              {open === i && (
                <motion.p 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="text-gray-400 mt-3 text-sm"
                >
                  {faq.a}
                </motion.p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};